/* eslint-disable prefer-promise-reject-errors */
import axios from 'axios'
import store from '@/store'
import configuration from '@/configuration'
import { get } from 'lodash'
import router from '@/router'
import localStoreKeys from '@/config/localStoreKeys'

/**
 * @typedef {Object} ErrorDetail
 * @property {string} code - 錯誤代碼
 * @property {string} msg - 錯誤訊息
 */

/**
 * API 回應格式 (成功時)
 * @template T
 * @typedef {[T, null]} SuccessResponse
 */

/**
 * API 回應格式 (失敗時)
 * @template T
 * @typedef {[null, string|ErrorDetail|number, T]} ErrorResponse
 */

/**
 * API 統一回應格式
 * @template T
 * @typedef {Promise<SuccessResponse<T> | ErrorResponse<any>>} Api2Response
 */

// 分店 context 遺失時 url 會變成 /null/... 或 /undefined/...，
// 這種 request 打到後端會讓 uuid 欄位查詢失敗（SequelizeDatabaseError），在前端直接擋掉 [CBGISSUE-259]
const MISSING_SHOP_CONTEXT = 'MISSING_SHOP_CONTEXT'
const invalidShopIdPathReg = /^\/(null|undefined)(\/|$)/

const getMissingShopContextError = (config) => {
  if (!invalidShopIdPathReg.test(get(config, 'url', ''))) return null

  console.error(`[api] block request with invalid shopId: ${get(config, 'url')}`)
  const error = new Error(MISSING_SHOP_CONTEXT)
  error.config = config
  error.response = {
    status: 400,
    data: {
      status: 400,
      type: MISSING_SHOP_CONTEXT,
      errorCode: MISSING_SHOP_CONTEXT,
      msg: '尚未選擇分店，請重新選擇分店後再操作',
    },
  }
  return error
}

// Session 失效時，當下所有併發中的 request 都會各自收到 401。若每一支都自己跳 toast +
// push('/login')，使用者會看到一連串重複的錯誤訊息，而且後一次 push 會「取消」前一次還在
// 進行中的導航 —— 初次導航一旦被取消，vue-router 會把 history 標記成 ready 卻只呼叫
// readyErrorCbs，之後成功的導航不會再補觸發 readyCbs，App.vue 的 loaded 永遠是 false，
// 畫面就卡在 AppLoading。因此同一個 token 的 401 只處理一次。
// 用 token 當 key 而非布林旗標，是為了讓使用者重新登入（換到新 token）後能自動重置。
const NOT_HANDLED_YET = Symbol('unauthorized not handled yet')
let unauthorizedHandledToken = NOT_HANDLED_YET

const handleUnauthorized = (message) => {
  const failedToken = store.getters.token

  // 移除 token 是冪等操作，每一支 401 都要做，確保 guard 之後讀到的是「已登出」狀態
  localStorage.removeItem(localStoreKeys.Token)

  if (unauthorizedHandledToken === failedToken) return
  unauthorizedHandledToken = failedToken

  // window.$message 在 App.vue mounted 才掛上，早於它的 401 不應該再炸一個 TypeError
  if (window.$message) window.$message.error(message)
  router.push({ name: 'Login' }).catch(() => {})
}

// --- handler ---
const handleSuccessRes = (response) => {
  return response
}
const handleErrorRes = (error) => {
  if (error.code === 'ECONNABORTED') {
    return Promise.reject('請求逾時')
  }
  if (!error.response) {
    return Promise.reject('系統錯誤')
  }
  const { data } = error.response
  const { type, msg, status } = data
  console.log(`err: ${type}, ${status}, ${msg}`) // for debug
  if (status === 401) {
    handleUnauthorized(msg)
    return Promise.reject(401)
  }
  if (status === 503) return Promise.reject(503)
  if (status === 403) {
    // ---- 權限錯誤
    // return Promise.reject(`${type}: ${msg}` || error.message)
  }
  return Promise.reject(`${msg}` || error.message)
}

/**
 * 成功回應處理器
 * @template T
 * @param {import('axios').AxiosResponse<T>} response
 * @returns {SuccessResponse<T>}
 */
const SuccessRes = (response) => {
  return [response.data, null]
}

/**
 * 錯誤回應處理器
 * @param {import('axios').AxiosError} error
 * @returns {ErrorResponse<any>}
 */
const ErrorRes = (error) => {
  if (axios.isCancel(error)) return Promise.reject(error)

  if (error.code === 'ECONNABORTED') {
    return [null, '請求逾時', get(error.response, 'data')]
  }

  if (!error.response) {
    return [null, '系統錯誤', get(error.response, 'data')]
  }
  const { data } = error.response
  const { type, msg, status, errorCode } = data
  console.log(`err: ${type}, ${status}, ${msg}`) // for debug
  if (errorCode === MISSING_SHOP_CONTEXT) {
    return [null, msg, get(error.response, 'data')]
  }
  if (errorCode === 'COUPON_EXPAT_MUST_LESS_THAN_ENDAT') {
    return [null, { code: errorCode, msg: '使用券過期於活動時間' }, get(error.response, 'data')]
  }
  if (status === 401) {
    handleUnauthorized(`${type}: ${msg}`)
  }
  if (status === 404) return [null, data, get(error.response, 'data')]
  // if (status === 403) {}
  if (status === 503) return [null, 503, get(error.response, 'data')]
  return [null, `${type}: ${msg}` || error.message, get(error.response, 'data')]
}

// --- admin ---
export const admin = axios.create({
  baseURL: `${configuration('backendHost')}/api-admin`,
})

admin.interceptors.request.use(async (config) => {
  const missingShopContextError = getMissingShopContextError(config)
  if (missingShopContextError) return Promise.reject(missingShopContextError)

  // 動態取得 vuex state 當前 token 設置為 Bearer Token
  config.headers.Authorization = `Bearer ${store.getters.token}`

  // const token = ls.getToken()
  // config.headers.Authorization =  `Bearer ${token}`

  return config
})
admin.interceptors.response.use(handleSuccessRes, handleErrorRes)

// --- admin 2 ---
/**
 * Admin API 實例 (返回 [data, err, rawErr] 格式)
 * @template T
 * @param {import('axios').AxiosRequestConfig} config
 * @returns {Api2Response<T>}
 */
export const admin2 = axios.create({
  baseURL: `${configuration('backendHost')}/api-admin`,
})

admin2.interceptors.request.use(async (config) => {
  const missingShopContextError = getMissingShopContextError(config)
  if (missingShopContextError) return Promise.reject(missingShopContextError)

  config.headers.Authorization = `Bearer ${store.getters.token}`
  return config
})
admin2.interceptors.response.use(SuccessRes, ErrorRes)

// --- admin public ---
export const adminPublic = axios.create({
  baseURL: `${configuration('backendHost')}/api-admin`,
})

// --- ohbot ---
export const ohbot = axios.create({
  baseURL: `${configuration('backendHost')}/api-ohbot`,
})

// --- public ---
export const publicApi = axios.create({
  baseURL: `${configuration('backendHost')}/api`,
})

// --- bob ---
/**
 * Bob Analytics API 實例 (返回 [data, err, rawErr] 格式)
 * @template T
 * @param {import('axios').AxiosRequestConfig} config
 * @returns {Api2Response<T>}
 */
export const bob = axios.create({
  baseURL: `${configuration('bobAnalyticsHost')}/api-bob`,
})
bob.interceptors.response.use(SuccessRes, ErrorRes)
// --- orgAdmin ---
/**
 * Organization Admin API 實例 (返回 [data, err, rawErr] 格式)
 * @template T
 * @param {import('axios').AxiosRequestConfig} config
 * @returns {Api2Response<T>}
 */
export const orgAdmin = axios.create({
  baseURL: `${configuration('backendHost')}/api-orgAdmin`,
})

orgAdmin.interceptors.request.use(async (config) => {
  config.headers.Authorization = `Bearer ${store.getters.token}`
  return config
})
orgAdmin.interceptors.response.use(SuccessRes, ErrorRes)
